/**
 * Authentication credential storage (single-row table pattern).
 */

import { createHash } from "node:crypto";
import { getEnv } from "../env.js";
import { withDbSpan } from "../telemetry.js";
import { getDatabase } from "./index.js";
import { clearAllIssueOrgCache } from "./issue-org-cache.js";
import { runUpsert } from "./utils.js";

/** Refresh when less than 10% of token lifetime remains */
export const REFRESH_THRESHOLD = 0.1;

/** Default token lifetime (1 hour) for tokens without issuedAt */
export const DEFAULT_TOKEN_LIFETIME_MS = 3600 * 1000;

type AuthRow = {
  token: string | null;
  refresh_token: string | null;
  expires_at: number | null;
  issued_at: number | null;
  updated_at: number;
};

/** Prefix for environment variable auth sources in {@link AuthSource} */
export const ENV_SOURCE_PREFIX = "env:";

/** Where the auth token originated */
export type AuthSource = "env:SENTRY_AUTH_TOKEN" | "env:SENTRY_TOKEN" | "oauth";

export type AuthConfig = {
  token?: string;
  refreshToken?: string;
  expiresAt?: number;
  issuedAt?: number;
  source: AuthSource;
};

/**
 * Read the raw token string from environment variables, ignoring all filters.
 *
 * Unlike {@link getEnvToken}, this always returns the env token if set, even
 * when stored OAuth credentials would normally take priority. Used by the HTTP
 * layer to check "was an env token provided?" independent of whether it's being
 * used, and by the per-endpoint permission cache.
 */
export function getRawEnvToken(): string | undefined {
  const authToken = getEnv().SENTRY_AUTH_TOKEN?.trim();
  if (authToken) {
    return authToken;
  }
  const sentryToken = getEnv().SENTRY_TOKEN?.trim();
  if (sentryToken) {
    return sentryToken;
  }
  return;
}

/**
 * Read token from environment variables.
 * `SENTRY_AUTH_TOKEN` takes priority over `SENTRY_TOKEN` (matches legacy sentry-cli).
 * Empty or whitespace-only values are treated as unset.
 *
 * This function is intentionally pure (no DB access). The "prefer stored OAuth
 * over env token" logic lives in {@link getAuthToken} and {@link getAuthConfig}
 * which check the DB first when `SENTRY_FORCE_ENV_TOKEN` is not set.
 */
function getEnvToken(): { token: string; source: AuthSource } | undefined {
  const authToken = getEnv().SENTRY_AUTH_TOKEN?.trim();
  if (authToken) {
    return { token: authToken, source: "env:SENTRY_AUTH_TOKEN" };
  }
  const sentryToken = getEnv().SENTRY_TOKEN?.trim();
  if (sentryToken) {
    return { token: sentryToken, source: "env:SENTRY_TOKEN" };
  }
  return;
}

/**
 * Check if authentication is coming from an environment variable.
 * Use this to skip refresh/OAuth logic that doesn't apply to env tokens.
 */
export function isEnvTokenActive(): boolean {
  return getEnvToken() !== undefined;
}

/**
 * Get the name of the env var providing a token, for error messages.
 * Returns the specific variable name (e.g. "SENTRY_AUTH_TOKEN" or "SENTRY_TOKEN")
 * by checking which env var {@link getRawEnvToken} would read.
 * Falls back to "SENTRY_AUTH_TOKEN" if no env var is set.
 */
export function getActiveEnvVarName(): string {
  // Match getRawEnvToken() priority: SENTRY_AUTH_TOKEN first, then SENTRY_TOKEN
  if (getEnv().SENTRY_AUTH_TOKEN?.trim()) {
    return "SENTRY_AUTH_TOKEN";
  }
  if (getEnv().SENTRY_TOKEN?.trim()) {
    return "SENTRY_TOKEN";
  }
  return "SENTRY_AUTH_TOKEN";
}

export function getAuthConfig(): AuthConfig | undefined {
  // When SENTRY_FORCE_ENV_TOKEN is set, check env first (old behavior).
  // Otherwise, check the DB first — stored OAuth takes priority over env tokens.
  // This is the core fix for #646: wizard-generated build tokens no longer
  // silently override the user's interactive login.
  const forceEnv = getEnv().SENTRY_FORCE_ENV_TOKEN?.trim();
  if (forceEnv) {
    const envToken = getEnvToken();
    if (envToken) {
      return { token: envToken.token, source: envToken.source };
    }
  }

  const dbConfig = withDbSpan("getAuthConfig", () => {
    const db = getDatabase();
    const row = db.query("SELECT * FROM auth WHERE id = 1").get() as
      | AuthRow
      | undefined;

    if (!row?.token) {
      return;
    }

    // Skip expired tokens without a refresh token — they're unusable.
    // Expired tokens WITH a refresh token are kept: auth refresh and
    // refreshToken() need them to perform the OAuth refresh flow.
    if (row.expires_at && Date.now() > row.expires_at && !row.refresh_token) {
      return;
    }

    return {
      token: row.token ?? undefined,
      refreshToken: row.refresh_token ?? undefined,
      expiresAt: row.expires_at ?? undefined,
      issuedAt: row.issued_at ?? undefined,
      source: "oauth" as const,
    };
  });
  if (dbConfig) {
    return dbConfig;
  }

  // No stored OAuth — fall back to env token
  const envToken = getEnvToken();
  if (envToken) {
    return { token: envToken.token, source: envToken.source };
  }
  return;
}

/**
 * Get the active auth token.
 *
 * Default: checks the DB first (stored OAuth wins), then falls back to env vars.
 * With `SENTRY_FORCE_ENV_TOKEN=1`: checks env vars first (old behavior).
 */
export function getAuthToken(): string | undefined {
  const forceEnv = getEnv().SENTRY_FORCE_ENV_TOKEN?.trim();
  if (forceEnv) {
    const envToken = getEnvToken();
    if (envToken) {
      return envToken.token;
    }
  }

  const dbToken = withDbSpan("getAuthToken", () => {
    const db = getDatabase();
    const row = db.query("SELECT * FROM auth WHERE id = 1").get() as
      | AuthRow
      | undefined;

    if (!row?.token) {
      return;
    }

    if (row.expires_at && Date.now() > row.expires_at) {
      return;
    }

    return row.token;
  });
  if (dbToken) {
    return dbToken;
  }

  // No stored OAuth — fall back to env token
  const envToken = getEnvToken();
  if (envToken) {
    return envToken.token;
  }
  return;
}

export function setAuthToken(
  token: string,
  expiresIn?: number,
  newRefreshToken?: string
): void {
  withDbSpan("setAuthToken", () => {
    const db = getDatabase();
    const now = Date.now();
    const expiresAt = expiresIn ? now + expiresIn * 1000 : null;
    const issuedAt = expiresIn ? now : null;

    runUpsert(
      db,
      "auth",
      {
        id: 1,
        token,
        refresh_token: newRefreshToken ?? null,
        expires_at: expiresAt,
        issued_at: issuedAt,
        updated_at: now,
      },
      ["id"]
    );
  });
  // Auth row changed — drop the memoized fingerprint so the next
  // `getIdentityFingerprint()` call reflects the new row.
  resetIdentityFingerprintCache();
}

export async function clearAuth(): Promise<void> {
  withDbSpan("clearAuth", () => {
    const db = getDatabase();
    db.query("DELETE FROM auth WHERE id = 1").run();
    // Also clear user info, org region cache, pagination cursors, and the
    // issue-id → org cache (scoped to the current user's permissions) when
    // logging out.
    db.query("DELETE FROM user_info WHERE id = 1").run();
    db.query("DELETE FROM org_regions").run();
    db.query("DELETE FROM pagination_cursors").run();
    clearAllIssueOrgCache();
  });
  resetIdentityFingerprintCache();

  // Dynamic import avoids the auth→response-cache→auth cycle.
  try {
    const { clearResponseCache } = await import("../response-cache.js");
    await clearResponseCache();
  } catch {
    // Non-fatal: cache directory may not exist yet
  }
}

export function isAuthenticated(): boolean {
  const token = getAuthToken();
  return !!token;
}

/** Fingerprint returned when no token is present (logged out, no env var). */
export const ANON_IDENTITY = "<anon>";

/** Memoized fingerprint. Identity doesn't change within a single CLI run. */
let cachedFingerprint: string | undefined;

/**
 * Opaque fingerprint of the active bearer identity, used to namespace
 * response-cache keys so entries never leak across accounts. Mirrors
 * `getAuthConfig` precedence: forced env token > stored OAuth
 * (refresh_token preferred for stability across access-token rotation,
 * falling through expired access-only rows) > env token > anonymous.
 *
 * Memoized. Reset on every mutation point (`setAuthToken`,
 * `clearAuth`), so both the common case (OAuth access-token refresh
 * with a stable refresh_token — fingerprint unchanged in practice)
 * and the uncommon case (server-rotated refresh_token — fingerprint
 * changes, cache naturally re-populates under the new identity) work
 * correctly. Tests that mutate auth state between cases call
 * {@link resetIdentityFingerprintCache}.
 */
export function getIdentityFingerprint(): string {
  if (cachedFingerprint === undefined) {
    cachedFingerprint = computeIdentityFingerprint();
  }
  return cachedFingerprint;
}

/** Reset the memoized fingerprint. Tests only — call between auth-state mutations. */
export function resetIdentityFingerprintCache(): void {
  cachedFingerprint = undefined;
}

function computeIdentityFingerprint(): string {
  // Forced env-token: matches what `refreshToken()` will actually send.
  if (getEnv().SENTRY_FORCE_ENV_TOKEN?.trim()) {
    const envToken = getRawEnvToken();
    if (envToken) {
      return hashIdentity("env", envToken);
    }
  }

  const row = withDbSpan("getIdentityFingerprint", () => {
    const db = getDatabase();
    return db
      .query("SELECT token, refresh_token, expires_at FROM auth WHERE id = 1")
      .get() as
      | {
          token: string | null;
          refresh_token: string | null;
          expires_at: number | null;
        }
      | undefined;
  });
  // Prefer refresh_token: stable across access-token rotation.
  if (row?.refresh_token) {
    return hashIdentity("oauth", row.refresh_token);
  }
  // Access-only row: skip if expired (mirrors getAuthConfig).
  if (row?.token && !(row.expires_at && Date.now() > row.expires_at)) {
    return hashIdentity("oauth-access", row.token);
  }

  const envToken = getRawEnvToken();
  if (envToken) {
    return hashIdentity("env", envToken);
  }
  return ANON_IDENTITY;
}

/**
 * 16-char MD5 hex of `kind|secret`. Not used for auth — just a cheap
 * cache namespace. Collisions are benign (identities would share a
 * cache slot, same as the anonymous case).
 */
function hashIdentity(kind: string, secret: string): string {
  return createHash("md5")
    .update(`${kind}|${secret}`)
    .digest("hex")
    .slice(0, 16);
}

/**
 * Check if usable OAuth credentials are stored in the database.
 *
 * Returns true when the `auth` table has either:
 * - A non-expired token, or
 * - An expired token with a refresh token (will be refreshed on next use)
 *
 * Used by the login command to decide whether to prompt for re-authentication
 * when an env token is present.
 */
export function hasStoredAuthCredentials(): boolean {
  const db = getDatabase();
  const row = db.query("SELECT * FROM auth WHERE id = 1").get() as
    | AuthRow
    | undefined;
  if (!row?.token) {
    return false;
  }
  // Non-expired token
  if (!row.expires_at || Date.now() <= row.expires_at) {
    return true;
  }
  // Expired but has refresh token — will be refreshed on next use
  return !!row.refresh_token;
}

export type RefreshTokenOptions = {
  /** Bypass threshold check and always refresh */
  force?: boolean;
};

export type RefreshTokenResult = {
  token: string;
  refreshed: boolean;
  expiresAt?: number;
  expiresIn?: number;
};

let refreshPromise: Promise<RefreshTokenResult> | null = null;

async function performTokenRefresh(
  storedRefreshToken: string
): Promise<RefreshTokenResult> {
  const { refreshAccessToken } = await import("../oauth.js");
  const { AuthError } = await import("../errors.js");

  try {
    const tokenResponse = await refreshAccessToken(storedRefreshToken);
    const now = Date.now();
    const expiresAt = now + tokenResponse.expires_in * 1000;

    await setAuthToken(
      tokenResponse.access_token,
      tokenResponse.expires_in,
      tokenResponse.refresh_token ?? storedRefreshToken
    );

    return {
      token: tokenResponse.access_token,
      refreshed: true,
      expiresAt,
      expiresIn: tokenResponse.expires_in,
    };
  } catch (error) {
    // Only clear auth on explicit rejection, not network errors
    if (error instanceof AuthError) {
      await clearAuth();
    }
    throw error;
  }
}

/** Get a valid token, refreshing if needed. Use force=true after 401 responses. */
export async function refreshToken(
  options: RefreshTokenOptions = {}
): Promise<RefreshTokenResult> {
  // With SENTRY_FORCE_ENV_TOKEN, env token takes priority (no refresh needed).
  const forceEnv = getEnv().SENTRY_FORCE_ENV_TOKEN?.trim();
  if (forceEnv) {
    const envToken = getEnvToken();
    if (envToken) {
      return { token: envToken.token, refreshed: false };
    }
  }

  const { force = false } = options;
  const { AuthError } = await import("../errors.js");

  const db = getDatabase();
  const row = db.query("SELECT * FROM auth WHERE id = 1").get() as
    | AuthRow
    | undefined;

  if (!row?.token) {
    // No stored token — try env token as fallback
    const envToken = getEnvToken();
    if (envToken) {
      return { token: envToken.token, refreshed: false };
    }
    throw new AuthError("not_authenticated");
  }

  const now = Date.now();
  const expiresAt = row.expires_at;

  if (!expiresAt) {
    return { token: row.token, refreshed: false };
  }

  const issuedAt = row.issued_at ?? expiresAt - DEFAULT_TOKEN_LIFETIME_MS;
  const totalLifetime = expiresAt - issuedAt;
  const remainingLifetime = expiresAt - now;
  const remainingRatio = remainingLifetime / totalLifetime;
  const expiresIn = Math.max(0, Math.floor(remainingLifetime / 1000));

  if (!force && remainingRatio > REFRESH_THRESHOLD && now < expiresAt) {
    return {
      token: row.token,
      refreshed: false,
      expiresAt,
      expiresIn,
    };
  }

  if (!row.refresh_token) {
    await clearAuth();
    // Fall back to env token if available (consistent with getAuthToken/getAuthConfig)
    const envToken = getEnvToken();
    if (envToken) {
      return { token: envToken.token, refreshed: false };
    }
    throw new AuthError(
      "expired",
      "Session expired and no refresh token available. Run 'sentry auth login'."
    );
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = performTokenRefresh(row.refresh_token);
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}
